import { type RefObject, useCallback, useEffect, useRef, useState } from "react";

export interface UseCardCarouselReturn {
  /** Attach to the `<CardCarousel>` to wire scroll-position tracking. */
  ref: RefObject<HTMLDivElement | null>;
  /** True when the scroll position is past the start (i.e. the user can scroll left). */
  canScrollLeft: boolean;
  /** True when the scroll position has more content to the right. */
  canScrollRight: boolean;
  /** Smoothly scroll one page (the number of cards currently visible in the row) to the
   *  left. No-op when at the start. */
  scrollLeft: () => void;
  /** Smoothly scroll one page (the number of cards currently visible in the row) to the
   *  right. No-op when at the end. */
  scrollRight: () => void;
}

/** Scroll-position tracking + paged-scroll callbacks for `<CardCarousel>`. Use when the
 *  consumer needs to render `<` `>` arrow buttons (e.g. on wide containers where mouse
 *  users can't swipe). The hook owns the ref, so consumers attach `result.ref` to the
 *  carousel element rather than threading their own ref through. */
export function useCardCarousel(): UseCardCarouselReturn {
  const ref = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      setCanScrollLeft(el.scrollLeft > 1);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    });
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    const resize = new ResizeObserver(updateScrollState);
    resize.observe(el);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      resize.disconnect();
      cancelAnimationFrame(rafRef.current);
    };
  }, [updateScrollState]);

  const scrollByPage = useCallback((direction: "left" | "right") => {
    const el = ref.current;
    if (!el) return;
    const firstChild = el.querySelector<HTMLElement>(":scope > *");
    if (!firstChild) return;
    const cardWidth = firstChild.offsetWidth;
    const styles = getComputedStyle(el);
    const gap = parseFloat(styles.columnGap) || 32;
    const paddingX = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
    // Pages by the number of cards that currently fit in the visible row, so a full
    // set of cards scrolls in/out per click rather than a single card.
    const visibleCount = Math.max(1, Math.floor((el.clientWidth - paddingX + gap) / (cardWidth + gap)));
    const distance = visibleCount * (cardWidth + gap);
    el.scrollBy({ left: direction === "left" ? -distance : distance, behavior: "smooth" });
  }, []);

  const scrollLeft = useCallback(() => scrollByPage("left"), [scrollByPage]);
  const scrollRight = useCallback(() => scrollByPage("right"), [scrollByPage]);

  return { ref, canScrollLeft, canScrollRight, scrollLeft, scrollRight };
}
